import { useEffect } from "react";
import { findPrimitivesInDirection } from "../../utils/transactions/editor.utils";
import { Y_THRESHOLD } from "../../types/editor.constants";
import { ToastService } from "../../service/toast.service";

export interface NavigablePrimitive {
  id: string;
  x: number;
  y: number;
  type:
    | "function"
    | "primitive"
    | "condition"
    | "loop"
    | "process"
    | "break"
    | "return"
    | "throw"
    | "subtransaction";
  name: string;
  data: any;
}

interface UseKeyboardNavigationParams {
  primitives: NavigablePrimitive[];
  selectedIndex: number | null;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  onSelect: (index: number | null) => void;
  onOpenLibrary: (branch: "yes" | "no" | "after") => void;
  onDelete: () => void;
  onCloseEditPanel: () => void;
  disableEmptySelect?: boolean;
}

export function useKeyboardNavigation({
  primitives,
  selectedIndex,
  isEditMode,
  setIsEditMode,
  onSelect,
  onOpenLibrary,
  onDelete,
  onCloseEditPanel,
  disableEmptySelect = false,
}: UseKeyboardNavigationParams) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      const isArrow = [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ].includes(e.key);
      if (isInput) {
        if (e.ctrlKey && isArrow) {
        } else {
          if (!e.altKey) return;
          if (!isArrow) return;
          e.preventDefault();
        }
      }

      if (e.ctrlKey && (e.key === "e" || e.key === "у" || e.code === "KeyE")) {
        e.preventDefault();
        setIsEditMode((prev) => {
          const next = !prev;
          ToastService.info(next ? "Edit mode ON" : "Edit mode OFF");
          if (!next) onSelect(null);
          return next;
        });
        return;
      }

      if (!isEditMode) return;

      if (
        selectedIndex !== null &&
        e.code === "Escape" &&
        !e.altKey &&
        !isInput
      ) {
        e.preventDefault();
        onCloseEditPanel();
        return;
      }

      if (
        selectedIndex !== null &&
        e.code === "Delete" &&
        !e.altKey &&
        !isInput
      ) {
        e.preventDefault();
        onDelete();
        return;
      }

      if (selectedIndex !== null && e.altKey && !e.ctrlKey && isArrow) {
        const currentPrim = primitives[selectedIndex];
        if (!currentPrim) return;

        e.preventDefault();

        const directionMap: Record<string, "left" | "right" | "up" | "down"> = {
          ArrowLeft: "left",
          ArrowRight: "right",
          ArrowUp: "up",
          ArrowDown: "down",
        };

        const direction = directionMap[e.key];
        const found = findPrimitivesInDirection(
          primitives,
          currentPrim,
          direction,
          Y_THRESHOLD,
        );

        if (found.length > 0) {
          onSelect(found[0].idx);
        }
        return;
      }

      if (
        selectedIndex === null &&
        e.altKey &&
        !e.ctrlKey &&
        isArrow &&
        !disableEmptySelect
      ) {
        e.preventDefault();
        if (primitives.length > 0) onSelect(0);
        return;
      }

      if (selectedIndex !== null && e.ctrlKey && isArrow) {
        const prim = primitives[selectedIndex];
        if (!prim) return;

        if (prim.type === "condition") {
          const branchMap: Record<string, "no" | "yes" | "after"> = {
            ArrowUp: "yes",
            ArrowDown: "no",
            ArrowRight: "after",
          };
          const branch = branchMap[e.key];
          if (branch) {
            e.preventDefault();
            onOpenLibrary(branch);
          }
        } else if (prim.type === "loop") {
          if (e.code === "ArrowDown") {
            e.preventDefault();
            onOpenLibrary("yes");
          } else if (e.code === "ArrowRight") {
            e.preventDefault();
            onOpenLibrary("after");
          }
        } else if (e.code === "ArrowRight") {
          e.preventDefault();
          onOpenLibrary("after");
        }
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    primitives,
    selectedIndex,
    isEditMode,
    setIsEditMode,
    onSelect,
    onOpenLibrary,
    onDelete,
  ]);
}
